/**
 * Width rules of a rack interior (`RackInteriorOptions`): every row of
 * compartment widths, plus the dividers between them, fills the case width
 * (`width`). The blueprint builds the case from `width` and runs the last
 * compartment of a row to the wall, so these rules keep the stored rows equal
 * to what is built.
 */

/** Tray strength when a rack interior has no tray material yet. */
export const DEFAULT_TRAY_STRENGTH = 18;

/** Smallest compartment the editor accepts. */
export const MIN_COMPARTMENT_WIDTH = 1;

export type CompartmentRows = {
  levels: number[][];
  caseWidth: number;
};

/** Compartment widths plus the dividers between them. */
export function levelSpan(widths: readonly number[], divider: number): number {
  const compartments = widths.reduce((sum, width) => sum + width, 0);
  const dividers = Math.max(widths.length - 1, 0) * divider;
  return compartments + dividers;
}

/** Everything of a row up to its last compartment, the divider before it included. */
function prefixSpan(widths: readonly number[], divider: number): number {
  const prefix = widths.slice(0, -1);
  const sum = prefix.reduce((total, width) => total + width, 0);
  return sum + prefix.length * divider;
}

function copyLevels(levels: readonly (readonly number[])[]): number[][] {
  return levels.map((level) => [...level]);
}

/** The row with its last width set so that it fills `caseWidth`. */
function fittedLast(
  widths: readonly number[],
  caseWidth: number,
  divider: number,
): number[] | null {
  if (widths.length === 0) {
    return null;
  }
  const last = caseWidth - prefixSpan(widths, divider);
  if (last <= 0) {
    return null;
  }
  return [...widths.slice(0, -1), last];
}

/**
 * Every row fills `caseWidth`; its last compartment takes the difference.
 * `null` when a last compartment would be 0 mm or less.
 */
export function setCaseWidth(
  levels: readonly (readonly number[])[],
  caseWidth: number,
  divider: number,
): CompartmentRows | null {
  if (caseWidth <= 0 || levels.length === 0) {
    return null;
  }
  const next: number[][] = [];
  for (const level of levels) {
    const fitted = fittedLast(level, caseWidth, divider);
    if (!fitted) {
      return null;
    }
    next.push(fitted);
  }
  return { levels: next, caseWidth };
}

export type StoredLayoutFit = CompartmentRows & {
  /** The stored width was too narrow (or missing): the case grew to the widest row. */
  grown: boolean;
};

/**
 * Stored rows as they are built. The stored case width wins: each row is
 * fitted to it. Only when a row cannot fit (a last compartment would be 0 mm
 * or less), or no width is stored, every row is fitted to the widest row span
 * instead. `null` when not even that fits.
 */
export function fitStoredLayout(
  rows: readonly (readonly number[])[],
  storedWidth: number | undefined,
  divider: number,
): StoredLayoutFit | null {
  if (storedWidth !== undefined && storedWidth > 0) {
    const fitted = setCaseWidth(rows, storedWidth, divider);
    if (fitted) {
      return { ...fitted, grown: false };
    }
  }
  const widest = Math.max(0, ...rows.map((row) => levelSpan(row, divider)));
  const grown = setCaseWidth(rows, widest, divider);
  return grown ? { ...grown, grown: true } : null;
}

/**
 * Change one compartment width. The last compartment of a row sets the case
 * width; any other only the last width of its own row.
 */
export function setCompartmentWidth(
  levels: readonly (readonly number[])[],
  levelIndex: number,
  compartmentIndex: number,
  newWidth: number,
  divider: number,
  caseWidth: number,
): CompartmentRows | null {
  const level = levels[levelIndex];
  if (!level || newWidth <= 0 || compartmentIndex < 0) {
    return null;
  }
  if (compartmentIndex >= level.length) {
    return null;
  }
  const replaced = copyLevels(levels);
  if (compartmentIndex === level.length - 1) {
    const row = [...level.slice(0, -1), newWidth];
    replaced[levelIndex] = row;
    return setCaseWidth(replaced, levelSpan(row, divider), divider);
  }
  const row = level.map((width, index) =>
    index === compartmentIndex ? newWidth : width,
  );
  const fitted = fittedLast(row, caseWidth, divider);
  if (!fitted) {
    return null;
  }
  replaced[levelIndex] = fitted;
  return setCaseWidth(replaced, caseWidth, divider);
}

/**
 * Split a compartment in two. The divider comes out of the compartment, so
 * the row keeps its span; the odd millimetre stays left.
 */
export function splitCompartment(
  widths: readonly number[],
  compartmentIndex: number,
  divider: number,
): number[] | null {
  const width = widths[compartmentIndex];
  if (width === undefined) {
    return null;
  }
  const room = width - divider;
  const right = Math.floor(room / 2);
  const left = room - right;
  if (left <= 0 || right <= 0) {
    return null;
  }
  return [
    ...widths.slice(0, compartmentIndex),
    left,
    right,
    ...widths.slice(compartmentIndex + 1),
  ];
}

/**
 * Split a level height in two. The shelf comes out of the level, so the
 * interior height stays; the odd millimetre stays on top.
 */
export function splitLevelHeight(
  height: number,
  divider: number,
): [number, number] | null {
  const room = height - divider;
  const lower = Math.floor(room / 2);
  const upper = room - lower;
  if (upper <= 0 || lower <= 0) {
    return null;
  }
  return [upper, lower];
}

/**
 * A copy right after the compartment. The case grows by the copy plus one
 * divider, starting from `caseWidth`; the last compartments of the other rows
 * fill the new width.
 */
export function duplicateCompartment(
  levels: readonly (readonly number[])[],
  levelIndex: number,
  compartmentIndex: number,
  divider: number,
  caseWidth: number,
): CompartmentRows | null {
  const level = levels[levelIndex];
  const width = level?.[compartmentIndex];
  if (!level || width === undefined || width <= 0) {
    return null;
  }
  const next = copyLevels(levels);
  next[levelIndex] = [
    ...level.slice(0, compartmentIndex + 1),
    width,
    ...level.slice(compartmentIndex + 1),
  ];
  return setCaseWidth(next, caseWidth + width + divider, divider);
}

/** A removed compartment and one divider go to the last remaining compartment. */
export function removeCompartment(
  widths: readonly number[],
  compartmentIndex: number,
  divider: number,
): number[] | null {
  if (widths.length <= 1) {
    return null;
  }
  const removed = widths[compartmentIndex];
  if (removed === undefined || compartmentIndex < 0) {
    return null;
  }
  const rest = widths.filter((_, index) => index !== compartmentIndex);
  const last = rest.at(-1);
  if (last === undefined) {
    return null;
  }
  return [...rest.slice(0, -1), last + removed + divider];
}

/**
 * Smallest case width at which every last compartment keeps `minCompartment`
 * while the other compartments stay as they are.
 */
export function minimumCaseWidth(
  levels: readonly (readonly number[])[],
  divider: number,
  minCompartment = MIN_COMPARTMENT_WIDTH,
): number {
  let minimum = minCompartment;
  for (const level of levels) {
    if (level.length > 0) {
      minimum = Math.max(minimum, prefixSpan(level, divider) + minCompartment);
    }
  }
  return minimum;
}

/** A row as built at `caseWidth`, or as stored when it cannot be fitted. */
function builtRow(
  row: readonly number[],
  caseWidth: number,
  divider: number,
): number[] {
  return fittedLast(row, caseWidth, divider) ?? [...row];
}

/**
 * Smallest case width the rows can be scaled to ({@link scaleRowsToCaseWidth}):
 * every compartment keeps `minCompartment`. It depends only on how many
 * compartments a row has, so it stays the same for every frame of a drag.
 */
export function minimumScaledCaseWidth(
  rows: readonly (readonly number[])[],
  divider: number,
  minCompartment: number,
): number {
  // An empty row (or none) is one compartment as wide as the case.
  let minimum = minCompartment;
  for (const row of rows) {
    const count = Math.max(row.length, 1);
    minimum = Math.max(minimum, count * minCompartment + (count - 1) * divider);
  }
  return minimum;
}

function scaleRow(
  row: readonly number[],
  fromWidth: number,
  toWidth: number,
  divider: number,
  minCompartment: number,
): number[] | null {
  // A row that does not fill `fromWidth` (stored before the rule) scales from
  // what is built; a row that cannot be fitted scales from its own span.
  if (row.length === 0) {
    // Nothing stored: the level is built as one compartment to the wall.
    return [];
  }
  const base = builtRow(row, fromWidth, divider);
  const fromRoom = base.reduce((sum, width) => sum + width, 0);
  const toRoom = toWidth - (base.length - 1) * divider;
  if (fromRoom <= 0 || toRoom < base.length * minCompartment) {
    return null;
  }
  const scale = toRoom / fromRoom;
  const scaled: number[] = [];
  let used = 0;
  for (let index = 0; index < base.length - 1; index++) {
    const keepForRest = (base.length - 1 - index) * minCompartment;
    const width = Math.round((base[index] ?? 0) * scale);
    const capped = Math.min(
      Math.max(minCompartment, width),
      toRoom - used - keepForRest,
    );
    scaled.push(capped);
    used += capped;
  }
  scaled.push(toRoom - used);
  return scaled;
}

/**
 * Every row scaled proportionally to a new case width, the dividers kept.
 * Widths round to whole millimetres and the last compartment of each row takes
 * the rounding, so every row fills `toWidth` exactly. An unchanged width
 * returns the rows as they are: a drag released where it started must not
 * touch widths the user entered. `null` below {@link minimumScaledCaseWidth}.
 */
export function scaleRowsToCaseWidth(
  rows: readonly (readonly number[])[],
  fromWidth: number,
  toWidth: number,
  divider: number,
  minCompartment: number,
): number[][] | null {
  if (toWidth === fromWidth) {
    return copyLevels(rows);
  }
  const scaled: number[][] = [];
  for (const row of rows) {
    const next = scaleRow(row, fromWidth, toWidth, divider, minCompartment);
    if (!next) {
      return null;
    }
    scaled.push(next);
  }
  return scaled;
}
